import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'
import { useIbsCbsDescricoes } from '../hooks/useIbsCbsDescricoes'

interface ItemNotaEntrada {
  empcod: number
  serie: string
  numero: number
  tipoDoc: string
  dataEntrada: string | null
  dataEmissao: string | null
  chave: string | null
  cliCod: string
  fornecedor: string
  cnpjCpf: string
  itsCod: number
  descricao: string
  ncm: string
  cfop: number | null
  cfopDes: string
  cstRtc: string
  classTrib: string
  valorCbs: number
  valorIbsUf: number
  valorIbsMun: number
  quantidade: number
  valorUnitario: number
  valorItem: number
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function mesAtrasISO() {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 10)
}
function fmtData(v: string | null) { return v ? v.slice(0, 10).split('-').reverse().join('/') : '-' }
function fmtMoeda(v: number) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

function chaveDoc(it: ItemNotaEntrada) { return `${it.empcod}|${it.serie}|${it.numero}|${it.cliCod}` }

export default function RelatorioNotasEntrada() {
  const { empresaCod } = useEmpresa()
  const { cstDes, classTribDes } = useIbsCbsDescricoes()
  const [empcodDe, setEmpcodDe] = useState(String(empresaCod ?? 1))
  const [empcodAte, setEmpcodAte] = useState(String(empresaCod ?? 1))
  const [dataDe, setDataDe] = useState(mesAtrasISO())
  const [dataAte, setDataAte] = useState(hojeISO())
  const [classificacaoPor, setClassificacaoPor] = useState<'entrada' | 'emissao'>('entrada')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [tipoDoc, setTipoDoc] = useState('')
  const [cfop, setCfop] = useState('')
  const [cstRtc, setCstRtc] = useState('')
  const [totalPorCfop, setTotalPorCfop] = useState(true)
  const [totalIbsCbs, setTotalIbsCbs] = useState(true)

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [gerandoExcel, setGerandoExcel] = useState(false)
  const [erro, setErro] = useState('')
  const [itens, setItens] = useState<ItemNotaEntrada[] | null>(null)

  function filtrosParams() {
    return {
      empcodDe: empcodDe || undefined, empcodAte: empcodAte || undefined,
      dataDe: dataDe || undefined, dataAte: dataAte || undefined,
      classificacaoPor, fornecedorCod: fornecedorCod || undefined,
      tipoDoc: tipoDoc || undefined, cfop: cfop || undefined, cstRtc: cstRtc || undefined,
    }
  }

  async function gerar() {
    setCarregando(true); setErro(''); setItens(null)
    try {
      const r = await api.get('/relatorios-compras/notas-entrada', { params: filtrosParams() })
      setItens(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true); setErro('')
    try {
      const r = await api.get('/relatorios-compras/notas-entrada/pdf', {
        params: {
          ...filtrosParams(), fornecedorDes: fornecedorDes || undefined,
          totalPorCfop: totalPorCfop ? 'S' : 'N', totalIbsCbs: totalIbsCbs ? 'S' : 'N',
        },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  async function gerarExcel() {
    setGerandoExcel(true); setErro('')
    try {
      const r = await api.get('/relatorios-compras/notas-entrada/excel', {
        params: { ...filtrosParams(), totalPorCfop: totalPorCfop ? 'S' : 'N', totalIbsCbs: totalIbsCbs ? 'S' : 'N' },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'notas-entrada.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setErro('Erro ao gerar Excel.')
    } finally {
      setGerandoExcel(false)
    }
  }

  const totalItens = (itens ?? []).reduce((s, it) => s + it.valorItem, 0)

  const porCfop = new Map<string, { cfop: number | null; des: string; valor: number }>()
  for (const it of itens ?? []) {
    const k = String(it.cfop ?? '—')
    const acc = porCfop.get(k) ?? { cfop: it.cfop, des: it.cfopDes, valor: 0 }
    acc.valor += it.valorItem
    porCfop.set(k, acc)
  }
  const totCbs = (itens ?? []).reduce((s, it) => s + it.valorCbs, 0)
  const totIbsUf = (itens ?? []).reduce((s, it) => s + it.valorIbsUf, 0)
  const totIbsMun = (itens ?? []).reduce((s, it) => s + it.valorIbsMun, 0)

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
        Relação de Notas de Entrada
      </h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <Campo rotulo="Empresa De" width={90}>
            <input style={inp} inputMode="numeric" value={empcodDe} onChange={e => setEmpcodDe(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Até" width={90}>
            <input style={inp} inputMode="numeric" value={empcodAte} onChange={e => setEmpcodAte(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Período De" width={150}>
            <input style={inp} type="date" value={dataDe} onChange={e => setDataDe(e.target.value)} />
          </Campo>
          <Campo rotulo="Até" width={150}>
            <input style={inp} type="date" value={dataAte} onChange={e => setDataAte(e.target.value)} />
          </Campo>
          <Campo rotulo="Tipo (Data)" width={150}>
            <select style={inp} value={classificacaoPor} onChange={e => setClassificacaoPor(e.target.value as any)}>
              <option value="entrada">Data de Entrada</option>
              <option value="emissao">Data de Emissão</option>
            </select>
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={260}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Todos" listWidth={320} somenteComTexto
            />
          </Campo>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Tipo de Nota (opcional)" width={140}>
            <input style={inp} value={tipoDoc} placeholder="Ex: NFE, NSE, CTE"
              onChange={e => setTipoDoc(e.target.value.toUpperCase())} />
          </Campo>
          <Campo rotulo="CFOP (opcional)" width={110}>
            <input style={inp} inputMode="numeric" value={cfop} onChange={e => setCfop(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="CST/cClassTrib RTC (opcional)" width={160}>
            <input style={inp} value={cstRtc} onChange={e => setCstRtc(e.target.value)} />
          </Campo>
          <Campo rotulo="Total por CFOP" width={130}>
            <select style={inp} value={totalPorCfop ? 'S' : 'N'} onChange={e => setTotalPorCfop(e.target.value === 'S')}>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </Campo>
          <Campo rotulo="Total IBS/CBS" width={130}>
            <select style={inp} value={totalIbsCbs ? 'S' : 'N'} onChange={e => setTotalIbsCbs(e.target.value === 'S')}>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={carregando} onClick={gerar} style={{ height: 32 }}>
            {carregando ? 'Gerando...' : 'Gerar Relatório'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {itens && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>
            <button className="btn btn-sm" disabled={gerandoPdf} onClick={gerarPdf} style={{ height: 32 }}>
              {gerandoPdf ? 'Gerando...' : '📄 Gerar PDF'}
            </button>
            <button className="btn btn-sm" disabled={gerandoExcel} onClick={gerarExcel} style={{ height: 32 }}>
              {gerandoExcel ? 'Gerando...' : '📊 Gerar Excel'}
            </button>
          </div>

          {itens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhum item encontrado.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="table" style={{ width: '100%', fontSize: '0.76rem', marginBottom: '0.5rem' }}>
                  <thead>
                    <tr className="thead-claro" style={{ background: '#eef2f7' }}>
                      <th style={{ textAlign: 'center', width: 85 }}>Dt. Entrada</th>
                      <th style={{ textAlign: 'center', width: 85 }}>Dt. Emissão</th>
                      <th style={{ textAlign: 'center', width: 60 }}>Tipo</th>
                      <th style={{ textAlign: 'center', width: 45 }}>Sér</th>
                      <th style={{ textAlign: 'center', width: 75 }}>Número</th>
                      <th style={{ textAlign: 'left', width: 160 }}>Fornecedor</th>
                      <th style={{ textAlign: 'left', width: 110 }}>CNPJ/CPF</th>
                      <th style={{ textAlign: 'center', width: 70 }}>Cód. Item</th>
                      <th style={{ textAlign: 'left' }}>Descrição Item</th>
                      <th style={{ textAlign: 'center', width: 80 }}>NCM</th>
                      <th style={{ textAlign: 'center', width: 55 }}>CFOP</th>
                      <th style={{ textAlign: 'left', width: 160 }}>Descrição CFOP</th>
                      <th style={{ textAlign: 'left', width: 170 }}>CST/cClassTrib RTC</th>
                      <th style={{ textAlign: 'right', width: 85 }}>Vlr. CBS</th>
                      <th style={{ textAlign: 'right', width: 85 }}>Vlr. IBSUF</th>
                      <th style={{ textAlign: 'right', width: 85 }}>Vlr. IBSMun</th>
                      <th style={{ textAlign: 'right', width: 75 }}>Qtd.</th>
                      <th style={{ textAlign: 'right', width: 90 }}>Vlr. Unit.</th>
                      <th style={{ textAlign: 'right', width: 95 }}>Vlr. Item</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((it, idx) => (
                      <tr key={`${chaveDoc(it)}-${it.itsCod}-${idx}`} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                        <td style={{ textAlign: 'center' }}>{fmtData(it.dataEntrada)}</td>
                        <td style={{ textAlign: 'center' }}>{fmtData(it.dataEmissao)}</td>
                        <td style={{ textAlign: 'center' }}>{it.tipoDoc}</td>
                        <td style={{ textAlign: 'center' }}>{it.serie}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#1a6ea8' }}>{it.numero}</td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.fornecedor}>{it.fornecedor}</td>
                        <td>{it.cnpjCpf}</td>
                        <td style={{ textAlign: 'center' }}>{it.itsCod}</td>
                        <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.descricao}>{it.descricao}</td>
                        <td style={{ textAlign: 'center' }}>{it.ncm || '-'}</td>
                        <td style={{ textAlign: 'center' }}>{it.cfop ?? '-'}</td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.cfopDes}>{it.cfopDes || '-'}</td>
                        <td title={`${cstDes(it.cstRtc)} / ${classTribDes(it.cstRtc, it.classTrib)}`}>
                          {it.cstRtc ? `${it.cstRtc}${it.classTrib ? ' / ' + it.classTrib : ''}` : '-'}
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(it.valorCbs)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(it.valorIbsUf)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(it.valorIbsMun)}</td>
                        <td style={{ textAlign: 'right' }}>{it.quantidade}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(it.valorUnitario)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoeda(it.valorItem)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                      <td colSpan={18} style={{ textAlign: 'right', padding: '6px 8px' }}>TOTAL:</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totalItens)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {totalPorCfop && (
                <div style={{ marginTop: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                    Totais por CFOP (somados pelo CFOP do item)
                  </div>
                  <table className="table" style={{ width: '100%', maxWidth: 500, fontSize: '0.78rem' }}>
                    <thead>
                      <tr className="thead-claro" style={{ background: '#eef2f7' }}>
                        <th style={{ textAlign: 'left', width: 80 }}>CFOP</th>
                        <th style={{ textAlign: 'left' }}>Descrição</th>
                        <th style={{ textAlign: 'right', width: 140 }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...porCfop.entries()].sort((a, b) => (a[1].cfop ?? 0) - (b[1].cfop ?? 0)).map(([k, v]) => (
                        <tr key={k}>
                          <td>{v.cfop ?? '—'}</td>
                          <td>{v.des || '-'}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(v.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                        <td colSpan={2}>TOTAL</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalItens)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {totalIbsCbs && (
                <div style={{ marginTop: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                    Totais IBS/CBS
                  </div>
                  <table className="table" style={{ width: '100%', maxWidth: 320, fontSize: '0.78rem' }}>
                    <tbody>
                      <tr><td>CBS</td><td style={{ textAlign: 'right' }}>{fmtMoeda(totCbs)}</td></tr>
                      <tr><td>IBSUF</td><td style={{ textAlign: 'right' }}>{fmtMoeda(totIbsUf)}</td></tr>
                      <tr><td>IBSMun</td><td style={{ textAlign: 'right' }}>{fmtMoeda(totIbsMun)}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
